import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, KeyRound } from "lucide-react";

const FIELDS = {
  aws: [
    { k: "access_key_id", label: "Access Key ID", placeholder: "AKIA..." },
    { k: "secret_access_key", label: "Secret Access Key", type: "password" },
    { k: "region", label: "Region", placeholder: "us-east-1" },
  ],
  azure: [
    { k: "tenant_id", label: "Tenant ID" },
    { k: "client_id", label: "Client ID (App Registration)" },
    { k: "client_secret", label: "Client Secret", type: "password" },
    { k: "subscription_id", label: "Subscription ID" },
  ],
  gcp: [
    { k: "project_id", label: "Project ID" },
    { k: "service_account_json", label: "Service Account JSON", textarea: true,
      placeholder: '{ "type": "service_account", "project_id": "..." }' },
    { k: "organization_id", label: "Organization ID (optional, for SCC alerts)", optional: true },
  ],
  oracle: [
    { k: "tenancy_ocid", label: "Tenancy OCID" },
    { k: "user_ocid", label: "User OCID" },
    { k: "fingerprint", label: "API Key Fingerprint" },
    { k: "private_key_pem", label: "Private Key (PEM)", textarea: true,
      placeholder: "-----BEGIN PRIVATE KEY-----\\n..." },
    { k: "region", label: "Region", placeholder: "us-ashburn-1" },
  ],
  cloudflare: [
    { k: "api_token", label: "API Token", type: "password",
      placeholder: "Zone-scoped token with Firewall + Gateway:Edit" },
    { k: "account_id", label: "Account ID" },
  ],
  virtualexpanse: [
    { k: "api_endpoint", label: "API Endpoint", placeholder: "https://api.veconnect.com" },
    { k: "api_token", label: "API Token", type: "password" },
  ],
  omnissa: [
    { k: "base_url", label: "OIC API URL", placeholder: "https://api.omnissa.com" },
    { k: "api_token", label: "API Token", type: "password" },
  ],
};

export default function CredentialsDialog({ open, onClose, provider, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!open || !provider) return;
    setForm({});
    api.get("/cloud-credentials").then(({ data }) => {
      const found = data.find((d) => d.provider === provider);
      setExisting(found || null);
    }).catch(() => {});
  }, [open, provider]);

  if (!provider) return null;
  const fields = FIELDS[provider] || [];

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.post("/cloud-credentials", { provider, creds: form });
      toast.success(`${provider.toUpperCase()} credentials saved & verified`);
      onSaved?.(res.data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${provider.toUpperCase()} credentials?`)) return;
    try {
      await api.delete(`/cloud-credentials/${provider}`);
      toast.success("Removed");
      setExisting(null);
      onSaved?.(null);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-cyan-400" /> Configure {provider.toUpperCase()} Credentials
          </DialogTitle>
        </DialogHeader>

        {existing && (
          <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-sm p-3 text-xs font-mono text-emerald-400 flex items-center gap-2" data-testid="existing-creds-badge">
            <CheckCircle2 className="w-4 h-4" />
            Currently configured (last update {existing.updated_at?.slice(0, 19).replace("T", " ")})
          </div>
        )}

        <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 text-[11px] font-mono text-amber-300 flex gap-2" data-testid="creds-warning">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Stored encrypted at-rest with Fernet. Saving will validate against the live cloud API
            via {provider === "aws" ? "STS GetCallerIdentity" : provider === "azure" ? "Resource Graph"
              : provider === "gcp" ? "Compute API list" : "Identity API"} before persisting.
          </span>
        </div>

        <div className="space-y-3 py-2" data-testid={`creds-form-${provider}`}>
          {fields.map((f) => (
            <div key={f.k}>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                {f.label}{f.optional && <span className="text-zinc-600"> · optional</span>}
              </Label>
              {f.textarea ? (
                <Textarea
                  data-testid={`creds-${provider}-${f.k}`}
                  value={form[f.k] || ""}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  placeholder={f.placeholder}
                  rows={6}
                  className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-xs"
                />
              ) : (
                <Input
                  data-testid={`creds-${provider}-${f.k}`}
                  type={f.type || "text"}
                  value={form[f.k] || ""}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  placeholder={f.placeholder}
                  className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-xs"
                />
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          {existing && (
            <Button variant="ghost" data-testid="creds-remove-btn" onClick={remove} className="text-red-400 hover:text-red-300">
              Remove
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="creds-save-btn" disabled={busy} onClick={save} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm font-semibold">
            {busy ? "VALIDATING..." : "VALIDATE & SAVE"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
